import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ShareSearchButtonProps {
  className?: string;
}

/**
 * ShareSearchButton - copies current URL to clipboard
 *
 * Shows a link icon and "Share this search" text.
 * On click, copies URL and shows brief "Link copied!" feedback.
 */
export function ShareSearchButton({ className }: ShareSearchButtonProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Reset after 2 seconds
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for browsers without clipboard API
      console.error("Failed to copy to clipboard:", error);
    }
  };

  return (
    <button
      onClick={handleShare}
      className={cn(
        "inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded",
        className
      )}
      aria-label={t("results.shareSearch")}
    >
      {copied ? (
        <>
          <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
          <span className="text-green-600">{t("results.linkCopied")}</span>
        </>
      ) : (
        <>
          <Link2 className="h-4 w-4" aria-hidden="true" />
          <span>{t("results.shareSearch")}</span>
        </>
      )}
    </button>
  );
}
